import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { MetaGamesEventType } from '../../../dto/metagames.types';
import { useCoinsAnimation } from '../../../hooks/useCoinsAnimation';
import { useHiddenBodyOverflow } from '../../../hooks/useHiddenBodyOverflow';
import { logToNewRelic } from '../../../services/debug';
import { useStores } from '../../../stores/useStores';
import { devError } from '../../../utils/log';

import { LoaderCover } from './LoaderCover';
import classes from './MinigamesDialog.module.scss';

export const MinigamesDialog = observer(() => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { minigamesStore, walletStore, userFreeSpinsStore, userStore, metagamesStore } = useStores();
  const { startAnimation } = useCoinsAnimation();

  const [isReady, setIsReady] = useState(false);

  useHiddenBodyOverflow(true);

  const onIframeLoad = () => {
    void logToNewRelic(
      'MinigamesDialog iframe loaded',
      'MinigamesDialog iframe loaded: ' +
        JSON.stringify({
          src: iframeRef.current?.src,
        }),
      'debug',
    );
  };

  useEffect(() => {
    const eventHandler = (event: MessageEvent) => {
      try {
        const { type, payload } = event.data;
        switch (type) {
          case 'gameReady': {
            void logToNewRelic(
              'MinigamesDialog game ready',
              'MinigamesDialog game ready: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'debug',
            );
            minigamesStore.setCurrentGameType(payload?.gameType);
            break;
          }
          case 'spinEnd': {
            const scAmount = payload?.rewards.find((r: any) => r.currencyCode === 'SC')?.amount || 0;
            const gcAmount = payload?.rewards.find((r: any) => r.currencyCode === 'GC')?.amount || 0;
            const metagameTokenReward = payload?.rewards.find((r: any) => r.currencyCode === 'METAGAME_TOKEN');
            const { metagameTokenAmount, metagameTokenName, metagameTokenMode } = metagameTokenReward || {};
            let currencyName;
            if (metagameTokenName) {
              switch (metagameTokenName) {
                case MetaGamesEventType.HAMMERS:
                  currencyName = MetaGamesEventType.HAMMERS;
                  break;
                case MetaGamesEventType.PUZZLE:
                  currencyName = MetaGamesEventType.PUZZLE;
                  break;
                case MetaGamesEventType.TRAIL:
                  currencyName = MetaGamesEventType.TRAIL;
                  break;
                case MetaGamesEventType.PLINKO:
                  currencyName = MetaGamesEventType.PLINKO;
                  break;
                case MetaGamesEventType.ROULETTE:
                  currencyName = MetaGamesEventType.ROULETTE;
                  break;
                default:
                  currencyName = undefined;
              }
            }

            startAnimation({
              scAmount: Number(scAmount),
              gcAmount: Number(gcAmount),
              metagamesTokensAmount: Number(metagameTokenAmount) || 0,
              currencyName,
              coinType: metagameTokenMode,
            });
            void walletStore.fetchWallet();
            void metagamesStore.fetch();
            void userFreeSpinsStore.fetch();
            break;
          }
          case 'closeGame': {
            void userFreeSpinsStore.fetch().finally(() => {
              minigamesStore.closeMinigame();
              setIsReady(false);
            });

            break;
          }
          case 'claimedGift': {
            const scAmount = payload?.find((r: any) => r.coinType === 'SC')?.coinValue || 0;
            const gcAmount = payload?.find((r: any) => r.coinType === 'GC')?.coinValue || 0;
            const metagameTokenReward = payload?.find(
              (r: any) => r.currencyCode === 'METAGAME_TOKEN' || r.coinType === 'METAGAME_TOKEN',
            );
            const { metagameTokenAmount, metagameTokenName, metagameTokenMode, amount, currencyName, coinType } =
              metagameTokenReward || {};
            let currencyNameValue;
            let coinTypeValue;
            let metagamesTokensAmount = 0;

            if (metagameTokenName || currencyName) {
              currencyNameValue = metagameTokenName || currencyName;
              coinTypeValue = metagameTokenMode || coinType;
              metagamesTokensAmount = Number(metagameTokenAmount || amount || 0);
            }

            void walletStore.fetchWallet();
            void metagamesStore.fetch();
            startAnimation({
              scAmount: Number(scAmount),
              gcAmount: Number(gcAmount),
              metagamesTokensAmount,
              currencyName: currencyNameValue,
              coinType: coinTypeValue,
              onEnd: async () => {
                await userFreeSpinsStore.fetch();
                minigamesStore.closeMinigame();
                setIsReady(false);
              },
            });
            break;
          }
          case 'authenticationComplete': {
            setIsReady(true);
            break;
          }
          case 'authenticationError': {
            setIsReady(true);
            void logToNewRelic(
              'MinigamesDialog authentication error FE',
              'MinigamesDialog authentication error FE: ' +
                JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'error',
            );
            break;
          }
          case 'generalError': {
            void logToNewRelic(
              'MinigamesDialog general error FE',
              'MinigamesDialog general error FE: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'error',
            );
            minigamesStore.closeMinigame();
            break;
          }
          case 'logMessage': {
            void logToNewRelic(
              'MinigamesDialog log message',
              'MinigamesDialog log message: ' + JSON.stringify({ payload, userId: userStore.profile?.userId }),
              'debug',
            );
            break;
          }
        }
      } catch (e) {
        devError('MinigamesDialog eventHandler error', e);
      }
    };

    window.addEventListener('message', eventHandler);

    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, [minigamesStore, startAnimation, userFreeSpinsStore, userStore.profile?.userId, walletStore, metagamesStore]);

  return (
    <div className={classes.minigamesDialog}>
      <div className={classes.cover}>
        <div className={classes.dialog}>
          <iframe
            ref={iframeRef}
            className={classNames(classes.iframe, !isReady && classes.prepare)}
            src={minigamesStore.iframeData?.src || ''}
            title="Minigames"
            onLoad={onIframeLoad}
          />
        </div>
        {!isReady && <LoaderCover />}
      </div>
    </div>
  );
});
